import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { revenueRecognitionOptions } from 'common/types/revenueRecognition.types'
import { useShouldShowXeroMappings } from 'modules/Products/hooks/useShouldShowXeroMappings'
import { useXeroLedgerAccountOptions } from 'modules/Products/hooks/useXeroLedgerAccountOptions'
import { useSyncFormValidity } from '../../../editors/common/useSyncFormValidity'

export const useProductForm = () => {
  const flags = useFeatureFlags()
  const pricingEditorContext = usePricingEditorDomainContext()
  const showXeroMappings = useShouldShowXeroMappings()
  const {
    isLoading: isXeroMappingsLoading,
    hasMappings: hasXeroMappings,
    deferredRevenueOptions,
    recognizedRevenueOptions,
    unbilledRevenueOptions,
    initialDeferredRevenueMappingId,
    initialRecognizedRevenueMappingId,
    initialUnbilledRevenueMappingId
  } = useXeroLedgerAccountOptions()

  // Deferred/recognized selection is only required when the mapping dropdowns
  // are actually rendered — i.e. the feature is on AND the account has mappings
  // to choose from. With no mappings the banner shows instead, so requiring the
  // (hidden) fields would leave the form permanently invalid.
  const xeroMappingsAvailable = showXeroMappings && hasXeroMappings

  const { data: taxCategoriesData } = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxCategories
  )

  const taxCategories =
    taxCategoriesData?.pages?.flatMap(page => page.items) ?? []

  const defaultTaxCategory = taxCategories.find(({ isDefault }) => isDefault)

  const productFormValues = pricingEditorContext.queries.rawData.data.product

  const initialValue = {
    ...productFormValues,
    taxCategoryId: productFormValues.taxCategoryId ?? defaultTaxCategory?.id,
    deferredRevenueXeroAccountMappingId:
      productFormValues.deferredRevenueXeroAccountMappingId ??
      initialDeferredRevenueMappingId,
    recognizedRevenueXeroAccountMappingId:
      productFormValues.recognizedRevenueXeroAccountMappingId ??
      initialRecognizedRevenueMappingId,
    unbilledRevenueXeroAccountMappingId:
      productFormValues.unbilledRevenueXeroAccountMappingId ??
      initialUnbilledRevenueMappingId
  }

  const { fields, queries } = useForm({
    value: initialValue,
    reinitializeOnValueChange: ({ newData, prevData }) => {
      if (!prevData) return true
      return (
        (!prevData.taxCategoryId && Boolean(newData.taxCategoryId)) ||
        (!prevData.deferredRevenueXeroAccountMappingId &&
          Boolean(newData.deferredRevenueXeroAccountMappingId)) ||
        (!prevData.recognizedRevenueXeroAccountMappingId &&
          Boolean(newData.recognizedRevenueXeroAccountMappingId)) ||
        (!prevData.unbilledRevenueXeroAccountMappingId &&
          Boolean(newData.unbilledRevenueXeroAccountMappingId))
      )
    },
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'label'
      },
      {
        property: 'taxCategoryId',
        validation: [required]
      },
      {
        property: 'revenueRecognitionMethod',
        disabled: () => !flags.useRevRec,
        options: revenueRecognitionOptions
      },
      {
        property: 'deferredRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: deferredRevenueOptions,
        validation: xeroMappingsAvailable ? [required] : []
      },
      {
        property: 'recognizedRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: recognizedRevenueOptions,
        validation: xeroMappingsAvailable ? [required] : []
      },
      {
        // Unbilled revenue is an optional product-level override, unlike
        // deferred and recognized which are required when Xero mappings show.
        property: 'unbilledRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: unbilledRevenueOptions,
        validation: []
      }
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    onChange: newData =>
      pricingEditorContext.mutators.common.updateProduct({
        name: newData.name,
        label: newData.label,
        taxCategoryId: newData.taxCategoryId,
        revenueRecognitionMethod: newData.revenueRecognitionMethod,
        deferredRevenueXeroAccountMappingId:
          newData.deferredRevenueXeroAccountMappingId,
        recognizedRevenueXeroAccountMappingId:
          newData.recognizedRevenueXeroAccountMappingId,
        unbilledRevenueXeroAccountMappingId:
          newData.unbilledRevenueXeroAccountMappingId
      })
  })

  // The list price layout has no "Show additional fields" section.
  useSyncFormValidity({
    isFormValid: queries.isValid,
    formValidationResults: queries.validationResults,
    additionalFields: []
  })

  return {
    showXeroMappings,
    isXeroMappingsLoading,
    hasXeroMappings,
    fieldsConfig: {
      ...fields,
      taxCategoryId: {
        ...fields.taxCategoryId,
        hidden: false
      }
    }
  }
}
